// The providers every Moved Page needs, mounted once in src/App.js inside the
// Legacy AuthProvider, next to the Legacy react-query v3 client (ADR 0005).
// Imports http/error and http/legacySession only: src/App.js is loaded by
// Jest 27, which cannot parse openapi-fetch.
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useState } from "react";
import type { ReactNode } from "react";
import { AppApiError } from "./http/error";
import { useLegacySessionKey } from "./http/legacySession";

// A 4xx is the server's answer and asking again will not change it; a network
// failure or a 5xx is retried, as TanStack does by default.
const retry = (failureCount: number, error: unknown): boolean =>
  !(error instanceof AppApiError && error.status >= 400 && error.status < 500) && failureCount < 3;

export function createAppQueryClient(): QueryClient {
  return new QueryClient({ defaultOptions: { queries: { retry } } });
}

// Empties the cache at a session boundary. Each query is reset before it is
// removed: the reset cancels a fetch still in flight, so its response never
// lands, and tells every mounted observer that its data is gone. A mounted
// useQuery then reads a fresh, empty query and, when enabled, refetches under
// the new session. Mutations cannot be cancelled: the cache forgets them, but
// a mounted useMutation keeps its last result until it remounts.
export function resetAppSession(client: QueryClient): void {
  const queries = client.getQueryCache();
  for (const query of queries.getAll()) {
    query.reset();
    queries.remove(query);
  }
  client.getMutationCache().clear();
}

export function AppProviders({ children }: { children?: ReactNode }) {
  // One client for the life of the tab. Logout and login do not reload the
  // page, so the cache is reset when the session changes, during the render
  // that first sees the new session: no consumer rendered after it reads the
  // previous user's or organization's data. The Legacy tree under this
  // provider is not remounted.
  const sessionKey = useLegacySessionKey();
  const [client] = useState(createAppQueryClient);
  const [session, setSession] = useState(sessionKey);
  if (session !== sessionKey) {
    setSession(sessionKey);
    resetAppSession(client);
  }
  return <QueryClientProvider client={client}>{children}</QueryClientProvider>;
}
